import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import { getSituationStyle, Client } from '../page';
import ClientUsersTab from './components/ClientUsersTab';
import ClientProjectsTab from './components/ClientProjectsTab';

type TabId = 'overview' | 'projetos' | 'usuarios';

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuthContext();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const loadClient = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) {
      navigate('/admin/clientes');
      return;
    }
    setClient(data);
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      </AppLayout>
    );
  }

  if (!client) return null;

  const sit = getSituationStyle(client.situation);

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'overview', label: 'Visão Geral', icon: 'ri-information-line' },
    { id: 'projetos', label: 'Projetos', icon: 'ri-folder-line' },
    { id: 'usuarios', label: 'Usuários', icon: 'ri-team-line' },
  ];

  const hasAddress = client.address_street || client.address_city;

  return (
    <AppLayout>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
        <Link to="/admin/clientes" className="hover:text-slate-600 transition-colors cursor-pointer">
          Clientes
        </Link>
        <i className="ri-arrow-right-s-line text-xs"></i>
        <span className="text-slate-700 font-medium">{client.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-cyan-50 border border-cyan-100 shrink-0">
            <i className="ri-building-line text-cyan-500 text-2xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-slate-800 text-xl font-bold">{client.name}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sit.color}`}>
                {sit.label}
              </span>
              {!client.is_active && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                  Inativo
                </span>
              )}
            </div>
            <p className="text-slate-500 text-sm mt-0.5">
              {client.segment && <span>{client.segment}</span>}
              {client.segment && client.document && <span className="mx-1">·</span>}
              {client.document && <span>{client.document_type?.toUpperCase()} {client.document}</span>}
            </p>
          </div>
        </div>
        <Link
          to="/admin/clientes"
          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-sm"></i>
          Voltar
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-cyan-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dados de contato */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-slate-700 font-semibold text-sm mb-4 flex items-center gap-2">
              <i className="ri-contacts-line text-cyan-500"></i>
              Dados de Contato
            </h3>
            <div className="space-y-3">
              {client.contact_name && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-slate-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Contato principal</p>
                    <p className="text-slate-700 text-sm font-medium">{client.contact_name}</p>
                  </div>
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-slate-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">E-mail</p>
                    <p className="text-slate-700 text-sm">{client.email}</p>
                  </div>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-slate-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Telefone</p>
                    <p className="text-slate-700 text-sm">{client.phone}</p>
                  </div>
                </div>
              )}
              {client.whatsapp && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-whatsapp-line text-slate-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">WhatsApp</p>
                    <p className="text-slate-700 text-sm">{client.whatsapp}</p>
                  </div>
                </div>
              )}
              {client.website && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-global-line text-slate-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Website</p>
                    <a
                      href={client.website}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-cyan-600 text-sm hover:underline"
                    >
                      {client.website}
                    </a>
                  </div>
                </div>
              )}
              {!client.contact_name && !client.email && !client.phone && !client.whatsapp && (
                <p className="text-slate-400 text-sm">Nenhum dado de contato cadastrado</p>
              )}
            </div>
          </div>

          {/* Endereço */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-slate-700 font-semibold text-sm mb-4 flex items-center gap-2">
              <i className="ri-map-pin-line text-cyan-500"></i>
              Endereço
            </h3>
            {hasAddress ? (
              <div className="space-y-1 text-sm text-slate-700">
                {client.address_street && (
                  <p>{client.address_street}{client.address_number ? `, ${client.address_number}` : ''}{client.address_complement ? ` — ${client.address_complement}` : ''}</p>
                )}
                {client.address_neighborhood && <p>{client.address_neighborhood}</p>}
                {(client.address_city || client.address_state) && (
                  <p>{[client.address_city, client.address_state].filter(Boolean).join(' — ')}</p>
                )}
                {client.address_zip && <p>CEP: {client.address_zip}</p>}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Endereço não cadastrado</p>
            )}
          </div>

          {/* Observações */}
          {client.notes && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
              <h3 className="text-slate-700 font-semibold text-sm mb-3 flex items-center gap-2">
                <i className="ri-file-text-line text-cyan-500"></i>
                Observações
              </h3>
              <p className="text-slate-600 text-sm whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'projetos' && (
        <ClientProjectsTab clientId={client.id} clientName={client.name} />
      )}

      {activeTab === 'usuarios' && (
        <ClientUsersTab clientId={client.id} />
      )}
    </AppLayout>
  );
}
